import React, { useContext, useMemo } from 'react';

import { OneOnOneInstanceContext as DeerOneOnOneInstanceContext } from 'Deer/pages/MyData/OneOnOne/LegacyInstancePage/LegacyInstancePage';
import { StyledAvatar } from 'components/avatars';
import TextParseComponent from 'components/feed/TextParseComponent';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { OneOnOneInstanceContext } from 'screens/oneOnOne/instancePage/InstancePageContext';
import useLevelUpSidePanelContext from 'screens/oneOnOne/levelUp/competencySidePanel/context';
import { selectLoggedInEmployeeData, selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import { StyledTypography, StrikethroughCSS1, FlexContainer, VerticallyCenterContainer } from 'styles/common';
import { checkLevelUpFeatureFlagEnabled, checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { AGENDA_TAG_LABEL_TYPES, TASK_TYPES_2 } from 'constants/ProjectConstants';
import { UncheckedCircle } from 'constants/icons';
import { sendCompletedOneononeAgenda } from 'constants/mixpanelHelper';
import theme from 'constants/theme';

import { showTaskDetails } from 'services/taskDetails';

import { StyledNodesView, SubTaskCheck } from '../sidepanelStyles';
import AgendaMenuOptions from './AgendaMenuOptions';
import AgendaTag from './AgendaTag';
import { RightArrowIcon } from './commonComponents';
import { getCompletedColor } from './helper';

const TaggedAgenda = function ({ agendasDispatch, agenda, onCheckToggle, onDelete, isPlugin = false, filterEmpIds }) {
  const { userRole } = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId, shallowEqual);
  const contextToUse = checkNewNavEnabled() ? DeerOneOnOneInstanceContext : OneOnOneInstanceContext;

  const { openLevelUpSidePanel } = useLevelUpSidePanelContext();

  const {
    state: { instanceDetails, reportee },
  } = useContext(contextToUse);

  const checkInInstanceId = instanceDetails?.id;
  const checkInParentId = instanceDetails?.parentTaskId;

  const isManager = instanceDetails.from.id === loggedInEmployeeId;

  const dispatch = useDispatch();
  const currentOwners = useMemo(
    () => [
      {
        ...agenda.fromEmployee,
        image: agenda.fromEmployee.profilePhotoSrc,
        fullName: agenda.fromEmployee.displayName,
      },
    ],
    [agenda.fromEmployee]
  );

  const handleClick = (ev) => {
    ev.stopPropagation();

    if (!checkLevelUpFeatureFlagEnabled()) return;

    const { labelType, uuid } = agenda?.label;
    if (!labelType || !uuid) return;

    switch (labelType) {
      case AGENDA_TAG_LABEL_TYPES.LABEL_TASK.value:
        dispatch(
          showTaskDetails({
            taskIds: [agenda?.label?.uuid],
            onDelete: null,
            employee: {},
            onUpdate: (task) => {
              // TODO: handle task change.
            },
            type: TASK_TYPES_2.task,
          })
        );
        break;
      case AGENDA_TAG_LABEL_TYPES.LABEL_GOAL.value:
        dispatch(
          showTaskDetails({
            taskIds: [agenda?.label?.uuid],
            onDelete: null,
            employee: {},
            onUpdate: (task) => {
              // TODO: handle task change.
            },
            type: TASK_TYPES_2.goal,
          })
        );
        break;
      case AGENDA_TAG_LABEL_TYPES.LABEL_LEVELUP.value:
        openLevelUpSidePanel({
          reporteeId: reportee.id,
          competencyId: null,
          levelUpId: null,
          levelUpCompetencyId: uuid,
          isLoggedInEmployeeManager: isManager,
          checkInInstanceId,
          checkInParentId,
          filterEmpIds,
          isOneOnOne: true,
        });
        break;
      default:
        break;
    }
  };

  const handleCheckToggle = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    onCheckToggle(agenda);
    if (agenda.status !== 'COMPLETED') {
      sendCompletedOneononeAgenda({
        eventProperties: {
          role: userRole,
          type: AGENDA_TAG_LABEL_TYPES[agenda?.label?.labelType]?.displayName,
        },
      });
    }
  };
  return (
    <Container onClick={handleClick}>
      <LeftChildContainer>
        <SubTaskCheck
          onClick={handleCheckToggle}
          disableRipple
          checked={agenda.status === 'COMPLETED'}
          checkedIcon={<UncheckedCircle size="smaller" color={theme.palette.icons.blue} />}
          size="h6"
          icon={<UncheckedCircle size="smaller" color="textSecondary" />}
          style={{ padding: '0 1rem 0 0' }}
        />
        <VerticallyCenterContainer style={{ flexGrow: 1 }}>
          <TaskNameTypography
            variant="body2"
            display="inline"
            completed={agenda.status === 'COMPLETED'}
            textDecorationColor={getCompletedColor.call(agenda, 'textPrimary')}
          >
            <TextParseComponent text={agenda.text} res={agenda.macros} />
          </TaskNameTypography>
          <AgendaTag uuid={agenda?.label?.uuid} labelType={agenda?.label?.labelType} />
        </VerticallyCenterContainer>
      </LeftChildContainer>
      <RightChildContainer>
        <StyledNodesView
          height="100%"
          useEmployeeClick={false}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          employees={currentOwners}
          maxNodes={2}
          tooltipPrefix="Added by "
          renderAvatar={(props) => (
            <ImageContainer rightAligned={false}>
              <StyledAvatar {...props} size="smallest" leftGutter={-0.5} alt="" />
            </ImageContainer>
          )}
        />

        {!isPlugin && (
          <HoverOptionItems>
            <AgendaMenuOptions menuOptions={['DELETE']} agendaId={agenda?.uuid} onDelete={onDelete} />
            {checkLevelUpFeatureFlagEnabled() ? <StyledRightArrowIcon /> : null}
          </HoverOptionItems>
        )}
      </RightChildContainer>
    </Container>
  );
};

export default TaggedAgenda;

const StyledRightArrowIcon = styled(RightArrowIcon)`
  margin-left: 16px;
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  padding: ${(props) => props.padding};
  ${(props) =>
    props.alignSelf &&
    css`
      align-self: ${props.alignSelf};
    `}
`;

export const HoverOptionItems = styled(FlexContainer)`
  margin-left: 16px;
  justify-content: flex-end;
  visibility: hidden;
`;

const TaskNameTypography = styled(StyledTypography)`
  ${StrikethroughCSS1};
  padding-right: 0.5rem;
  padding-left: 0.5rem;
`;

const Container = styled.div`
  cursor: pointer;
  display: flex;
  margin: 0 -1rem 0 -1.5rem;
  padding: 0.5rem;
  justify-content: space-between;
  &:hover {
    background-color: ${(props) => props.theme.palette.background.lighterBlue};
    ${HoverOptionItems} {
      visibility: visible;
    }
  }
  ${(props) => props.theme.breakpoints.down('xs')} {
    margin: 0;
    padding: 16px 24px;
  }
`;

const LeftChildContainer = styled.div`
  flex: 1 1 auto;
  padding-right: 1rem;
  display: flex;
  align-items: center;
  margin-left: 1rem;
  ${(props) => props.theme.breakpoints.down('xs')} {
    margin: 0;
    flex-grow: 1;
    flex-basis: auto;
    padding-right: 24px;
  }
`;

const RightChildContainer = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-right: 1rem;
  ${(props) => props.theme.breakpoints.down('xs')} {
    margin: 0;
    height: 28px;
    width: 28px;
    flex-basis: auto;
  }
`;
